import React from 'react';
import { t } from '../../../utils/textUtils';
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormHelperText,
  Paper,
  Stack
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

const TeamForm = ({ formData, onChange, errors = {}, dropdownData = {} }) => {
  // Extract data from dropdownData prop
  const clients = dropdownData.clients || [];
  const productionCoordinators = dropdownData.productionCoordinators || [];
  const projectManagers = dropdownData.projectManagers || [];
  const siteManagers = dropdownData.siteManagers || [];
  const siteEngineers = dropdownData.siteEngineers || [];
  const handleUserChange = (field, value) => {
    onChange({ [field]: value });
  };

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: '#F8FAFC'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: '#ffffff',
              boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PeopleAltIcon sx={{ color: '#2563EB' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {t('TIM PELAKSANA')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Tentukan tim yang bertanggung jawab dalam pelaksanaan proyek.')}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            borderRadius: 2, 
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.1)',
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.5 }}>
              {t('TIM PROYEK')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Tentukan tim yang bertanggung jawab dalam pelaksanaan proyek.')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => option.name || ''}
                value={clients.find(client => client.id === formData.projectClientId) || null}
                onChange={(event, newValue) => {
                  handleUserChange('projectClientId', newValue ? newValue.id : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t('KLIEN')}
                    error={!!errors.projectClientId}
                    helperText={errors.projectClientId}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#3B82F6'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6'
                        }
                      }
                    }}
                  />
                )}
              />
              <FormHelperText sx={{ color: '#64748B' }}>
                {t('Pilih klien dari daftar klien yang tersedia')}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={projectManagers}
                getOptionLabel={(option) => option.fullName || option.username || ''}
                value={projectManagers.find(pm => pm.id === formData.managerId) || null}
                onChange={(event, newValue) => {
                  handleUserChange('managerId', newValue ? newValue.id : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t('MANAJER PROYEK')}
                    error={!!errors.managerId}
                    helperText={errors.managerId}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#3B82F6'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6'
                        }
                      }
                    }}
                  />
                )}
              />
              <FormHelperText sx={{ color: '#64748B' }}>
                {t('Pilih manajer proyek dari daftar pengguna dengan role project manager')}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={productionCoordinators}
                getOptionLabel={(option) => option.name || ''}
                value={productionCoordinators.find(coord => coord.id === formData.productionCoordinatorId) || null}
                onChange={(event, newValue) => {
                  handleUserChange('productionCoordinatorId', newValue ? newValue.id : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t('KOORDINATOR PRODUKSI')}
                    error={!!errors.productionCoordinatorId}
                    helperText={errors.productionCoordinatorId}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#3B82F6'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6'
                        }
                      }
                    }}
                  />
                )}
              />
              <FormHelperText sx={{ color: '#64748B' }}>
                {t('Pilih koordinator produksi dari daftar yang tersedia')}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={siteManagers}
                getOptionLabel={(option) => option.fullName || option.username || ''}
                value={siteManagers.find(sm => sm.id === formData.siteManagerId) || null}
                onChange={(event, newValue) => {
                  handleUserChange('siteManagerId', newValue ? newValue.id : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t('MANAJER LAPANGAN')}
                    error={!!errors.siteManagerId}
                    helperText={errors.siteManagerId}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#3B82F6'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6'
                        }
                      }
                    }}
                  />
                )}
              />
              <FormHelperText sx={{ color: '#64748B' }}>
                {t('Pilih manajer lapangan dari daftar pengguna dengan role site manager')}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={siteEngineers}
                getOptionLabel={option => option.fullName || option.username || ''}
                value={siteEngineers.filter(se => (formData.siteEngineerIds || []).includes(se.id))}
                onChange={(event, newValue) => {
                  const selectedIds = (newValue || []).map(item => item.id);
                  handleUserChange('siteEngineerIds', selectedIds);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t('INSINYUR LAPANGAN')}
                    error={!!errors.siteEngineerIds}
                    helperText={errors.siteEngineerIds}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#3B82F6'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6'
                        }
                      }
                    }}
                  />
                )}
              />
              <FormHelperText sx={{ color: '#64748B' }}>
                {t('Bisa memilih lebih dari satu insinyur lapangan')}
              </FormHelperText>
            </Grid>
          </Grid>
        </Paper>
    </Box>
  );
};

export default TeamForm;
